import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Row, Col, Spin, Pagination, Select } from 'antd';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import { LoadingOutlined } from '@ant-design/icons';
import CustomNavbar from '../components/Navbar';
import MatchCard from '../components/MatchCard';
import { useProfile } from '../context/ProfileContext';

const NonSearchableSelect = (props) => <Select {...props} showSearch={false} />;

const Matches = () => {
    const [navbarReady, setNavbarReady] = useState(false);
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pageSize, setPageSize] = useState(50);
    const [pagination, setPagination] = useState({ current: 1, total: 0 });
    const [totalOppositeProfiles, setTotalOppositeProfiles] = useState(null);

    const { profileId } = useProfile();

    // pageSize is a dependency — when it changes, fetchRecommendations gets a new
    // reference, which triggers the useEffect to re-fetch from page 1 automatically.
    const fetchRecommendations = useCallback(async (page = 1) => {
        if (!profileId) return;

        setLoading(true);
        setError(null);

        try {
            const response = await api.post('/matches/recommended', {
                profile_id: profileId,
                page,
                limit: pageSize,
            });

            if (response.data.status === 'success') {
                const paginatedData = response.data.data;
                const transformedProfiles = (paginatedData.data || []).map(profile => ({
                    ...profile,
                    picture: profile.picture
                        ? `${process.env.REACT_APP_IMAGE_URL}/${profile.picture}`
                        : (profile.gender?.toLowerCase() === 'female' ? process.env.REACT_APP_FEMALE_FALLBACK_IMAGE : process.env.REACT_APP_MALE_FALLBACK_IMAGE),
                }));
                setProfiles(transformedProfiles);
                setPagination({
                    current: paginatedData.current_page || 1,
                    total: paginatedData.total || 0,
                });
                setTotalOppositeProfiles(paginatedData.available_profiles);
            } else {
                setError(response.data.message || 'Failed to fetch recommendations');
            }
        } catch (err) {
            console.error('Error fetching recommendations:', err);
            setError(err.response?.data?.message || 'Failed to fetch recommendations');
        } finally {
            setLoading(false);
        }
    }, [profileId, pageSize]);

    useEffect(() => {
        if (navbarReady && profileId) {
            fetchRecommendations(1);
        }
    }, [navbarReady, profileId, fetchRecommendations]);

    const handlePageChange = (page, newPageSize) => {
        if (newPageSize !== pageSize) {
            // Changing page size: update state — useEffect re-runs via fetchRecommendations
            setPageSize(newPageSize);
        } else {
            fetchRecommendations(page);
        }
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px', minHeight: '300px' }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#AD1457' }} spin />} />
                </div>
            );
        }

        if (error) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px', minHeight: '300px' }}>
                    <p style={{ color: '#ff4d4f' }}>{error}</p>
                </div>
            );
        }

        if (!profiles.length) {
            return (
                <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    padding: '48px 24px', minHeight: '300px', textAlign: 'center',
                }}>
                    <p style={{ color: '#1A1A2E', fontSize: '1.1rem', fontWeight: 600, marginBottom: '8px' }}>
                        No matches found for your current preferences
                    </p>
                    {totalOppositeProfiles != null && totalOppositeProfiles > 0 && (
                        <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '20px', maxWidth: '480px', lineHeight: 1.6 }}>
                            There {totalOppositeProfiles === 1 ? 'is' : 'are'}{' '}
                            <strong>{totalOppositeProfiles.toLocaleString()}</strong>{' '}
                            {totalOppositeProfiles === 1 ? 'profile' : 'profiles'} on the platform, but none match your current filters.
                            Try relaxing some of your strict preferences — switch them to preferred so you can see more results.
                        </p>
                    )}
                    <Link
                        to="/partner-preferences"
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        style={{
                            display: 'inline-block', padding: '10px 28px', borderRadius: '8px',
                            background: 'linear-gradient(135deg, #AD1457, #D81B60)',
                            color: 'white', fontWeight: 600, fontSize: '0.9rem',
                            textDecoration: 'none', transition: 'opacity 0.2s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                    >
                        Update Partner Preferences
                    </Link>
                </div>
            );
        }

        return (
            <div>
                <Row gutter={[24, 24]}>
                    {profiles.map(profile => (
                        <Col key={profile.id} xs={24} sm={12} md={8} lg={6} xl={4}>
                            <MatchCard profile={profile} />
                        </Col>
                    ))}
                </Row>
                {pagination.total > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '32px', padding: '16px' }}>
                        <Pagination
                            current={pagination.current}
                            total={pagination.total}
                            pageSize={pageSize}
                            pageSizeOptions={[10, 20, 50]}
                            showSizeChanger
                            showQuickJumper={false}
                            selectComponentClass={NonSearchableSelect}
                            onChange={handlePageChange}
                            onShowSizeChange={handlePageChange}
                            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} profiles`}
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={{ fontFamily: 'Calibri, sans-serif', minHeight: '100vh' }}>
            <Helmet><title>My Matches | Oottan</title></Helmet>
            <CustomNavbar onNavbarReady={() => setNavbarReady(true)} />
            <div style={{ marginTop: '64px', padding: '24px' }}>
                {navbarReady && renderContent()}
            </div>
        </div>
    );
};

export default Matches;
